// React and hooks
import React, { useState, useEffect } from 'react';
// Icons
import { Trash } from 'lucide-react';
// API client
import api from '../../lib/api';
// Theme hook
import useTheme from '../../hooks/useTheme';
// i18n
import { useTranslation } from 'react-i18next';

const ReturnSaleModal = ({ isOpen, onClose, onSaved, sale }) => {
  // States
  const [barrels, setBarrels] = useState(['']);
  const [busy, setBusy] = useState(false);

  // Hooks
  const { theme } = useTheme();
  const { t } = useTranslation();

  // Base button styles (primary actions)
  const baseBtn =
    theme === 'dark'
      ? 'inline-flex items-center justify-center px-4 py-2 rounded-lg text-sm font-semibold shadow bg-white text-black hover:bg-black hover:text-white'
      : 'inline-flex items-center justify-center px-4 py-2 rounded-lg text-sm font-semibold shadow bg-black text-white hover:bg-white hover:text-black';

  // Small delete button (for barrel fields)
  const smallDeleteBtn =
    theme === 'dark'
      ? 'inline-flex items-center justify-center px-2 py-1 rounded-lg shadow bg-white text-black hover:bg-black hover:text-white'
      : 'inline-flex items-center justify-center px-2 py-1 rounded-lg shadow bg-black text-white hover:bg-white hover:text-black';

  // Load return barrels if already saved
  useEffect(() => {
    if (sale && Array.isArray(sale.returnBarrels) && sale.returnBarrels.length > 0) {
      setBarrels(sale.returnBarrels.map(String));
    } else {
      setBarrels(['']);
    }
  }, [sale]);

  // If modal is closed or no sale is provided, return null
  if (!isOpen || !sale) return null;

  // Clean and calculate totals
  const cleanedReturnBarrels = barrels
    .map((rb) => (typeof rb === 'string' ? rb.trim() : rb))
    .filter((rb) => rb !== '')
    .map((rb) => Number(rb))
    .filter((rb) => Number.isFinite(rb));

  const totalReturn = cleanedReturnBarrels.reduce((s, b) => s + b, 0);
  const sentWeight = parseFloat(sale.sentWeight) || 0;
  const remaining = sentWeight - totalReturn;

  // Save return data
  const handleSave = async () => {
    if (totalReturn < 0) return;

    setBusy(true);
    try {
      // Call backend -> backend will replace returnBarrels and returnWeight
      await api.put(`/sales/${sale.id}`, {
        productName: sale.productName,
        sentWeight: sentWeight,
        returnWeight: totalReturn,
        barrels: sale.barrels || [], // keep original barrels
        returnBarrels: cleanedReturnBarrels
      });
      onSaved();
      onClose();
    } catch (err) {
      console.error('Error updating return:', err);
    } finally {
      setBusy(false);
    }
  };

  // Handlers
  const handleBarrelChange = (i, value) => {
    const newList = [...barrels];
    newList[i] = value;
    setBarrels(newList);
  };

  const addBarrel = () => setBarrels([...barrels, '']);

  const removeBarrel = (i) => {
    if (barrels.length === 1) {
      // Clear last one instead of removing all
      const newList = [...barrels];
      newList[0] = '';
      setBarrels(newList);
      return;
    }
    const newList = [...barrels];
    newList.splice(i, 1);
    setBarrels(newList);
  };

  // Render
  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center">
      <div className="bg-white dark:bg-gray-900 p-6 rounded-lg shadow-lg w-96">
        {/* Modal title */}
        <h2 className="text-lg font-bold mb-4">{t('registerReturn')}</h2>

        {/* Product info */}
        <p className="mb-2">
          {t('product')}: <span className="font-semibold">{sale.productName}</span>
        </p>
        <p className="mb-4">
          {t('sentWeight')}: {sentWeight.toFixed(2)} KG
        </p>

        {/* Barrel inputs */}
        {barrels.map((w, i) => (
          <div key={i} className="flex gap-2 mb-2">
            <input
              type="number"
              placeholder={`${t('return')} ${i + 1} (kg)`}
              className="flex-1 p-2 border rounded 
                         bg-white text-black 
                         dark:bg-gray-900 dark:text-white dark:border-gray-600"
              value={w}
              onChange={(e) => handleBarrelChange(i, e.target.value)}
            />
            <button
              type="button"
              className={smallDeleteBtn}
              onClick={() => removeBarrel(i)}
            >
              <Trash size={14} />
            </button>
          </div>
        ))}

        {/* Add barrel button */}
        <button
          type="button"
          className="text-sm text-blue-600 mb-4"
          onClick={addBarrel}
        >
          + {t('addReturnBarrel')}
        </button>

        {/* Summary */}
        <div className="mt-4 p-3 border rounded bg-gray-100 dark:bg-gray-800 space-y-1 text-sm">
          {cleanedReturnBarrels.map((b, i) => (
            <p key={i}>
              <strong>{t('return')} {i + 1}:</strong> {b.toFixed(2)} KG
            </p>
          ))}
          <p>
            <strong>{t('totalReturn')}:</strong> {totalReturn.toFixed(2)} KG
          </p>
          <p>
            <strong>{t('remaining')}:</strong> {remaining.toFixed(2)} KG
          </p>
        </div>

        {/* Action buttons */}
        <div className="flex justify-end gap-2 mt-6">
          <button className={baseBtn} onClick={onClose}>{t('cancel')}</button>
          <button className={baseBtn} onClick={handleSave} disabled={busy}>
            {busy ? t('saving') : t('save')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReturnSaleModal;